import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { EVENT } from '../../config/constants';
import EventHandler from '../../helpers/EventHandler';
import DecodeGlobalContext from '../../redux/decodeGlobalContext';
import HomepageCard from '../../components/Cards/HomepageCard';
import Meta from '../../components/Meta';
import useHomepageEntry from '../../hooks/useHomepageEntry';
import useGlobalNavContent from '../../hooks/useGlobalNavContent';
import { isViewingArticle, isViewingHomepage } from '../../helpers/utils';
import gtmPush from '../../helpers/gtmPush';

import { SET_HOMEPAGE_POSTERS_QUERY_VARS } from '../../redux/actionTypes';

import './styles.scss';

const Home = ({ scrollY, categoryText = '' }) => {
  // Before doing anything else, check if we should throw a "not found" error.
  // There are two valid URL types:
  // - /decode (the Decode homepage)
  // - /decode/article/*
  // Note that a category filter can be applied to the homepage, like:
  // - /decode?category=*
  // But this is not a new path.

  const location = useLocation();

  if (!isViewingHomepage(location) && !isViewingArticle(location)) {
    throw new Error('No Entry');
  }

  const { state, dispatch } = useContext(DecodeGlobalContext);
  const { decodeHomepagePosters, title, loading } = useHomepageEntry();
  const navContents = useGlobalNavContent();
  const [isCopyExpanded, setIsCopyExpanded] = useState(false);
  const homepageRef = useRef();
  const postersLength = decodeHomepagePosters.length;
  const { homePagePosterQueryVars, homepageHeaderOffset, isTransported, isArticleCardsLoaded } =
    state;
  const { navigationItems } = navContents || {};
  const navItemData =
    navigationItems && navigationItems.length && categoryText.length
      ? navigationItems.find(item => item.slug === categoryText)
      : null;

  const homepageTransportClasses = ['homepage']
    .concat(isViewingArticle(location) && isArticleCardsLoaded ? 'homepage--transporter' : [])
    .concat(isTransported ? 'homepage--transported' : [])
    .join(' ');
  const categoryCopyClasses = ['homepage__copy-text']
    .concat(isCopyExpanded ? 'homepage__copy-text--expanded' : [])
    .join(' ');

  const handleScrollEvent = useCallback(() => {
    const { offsetHeight, offsetTop } = homepageRef.current;
    const homePageCardScrollHeight = offsetHeight - offsetTop;
    const currentScrollY = (window.scrollY || window.pageYOffset) + offsetTop;

    // trigger point: less than the twice of window height
    const scrollTriggerPoint = Math.floor(homePageCardScrollHeight - window.innerHeight * 2);
    if (
      !loading &&
      !categoryText.length &&
      currentScrollY >= scrollTriggerPoint &&
      homePagePosterQueryVars.offset < postersLength
    ) {
      dispatch({
        type: SET_HOMEPAGE_POSTERS_QUERY_VARS,
        payload: { offset: postersLength, limit: 1 }
      });
    }
  }, [homePagePosterQueryVars.offset, postersLength, loading]);

  if (navItemData) {
    document.title = `${navItemData.title} - DECODE`;
  }

  useEffect(() => {
    const __EVENT_SCROLL = EventHandler.subscribe(EVENT.SCROLL, handleScrollEvent);

    return () => EventHandler.unsubscribe(__EVENT_SCROLL);
  }, [handleScrollEvent]);

  useEffect(() => {
    dispatch({ type: 'GET_PAGE_TITLE', payload: title });
  }, [title]);

  useEffect(() => {
    // shift focus on page wrapper on route change.
    homepageRef.current.focus();
  }, [navItemData]);

  useEffect(() => {
    // If we're only on the homepage (not showing underneath an article) push a homepage view.
    if (!isViewingArticle(location)) {
      gtmPush({ event: 'gtm.messagePageView', contentType: 'Decode Homepage' });
    }
  }, [location]);

  return (
    <>
      <Meta active={!isViewingArticle(location)} />
      <div
        ref={homepageRef}
        className={homepageTransportClasses}
        tabIndex={-1}
        aria-label={navItemData ? navItemData.title : 'Homepage'}
        style={{
          transform:
            isViewingArticle(location) && scrollY
              ? `translateY(-${scrollY - homepageHeaderOffset * 2}px)`
              : false
        }}>
        {navItemData ? (
          <div className="homepage__category-container">
            <h1
              className="homepage__category-title"
              aria-live="polite"
              aria-label={navItemData.title}
              tabIndex={-1}>
              {navItemData.title}
            </h1>
            {navItemData.decodeCategoryDescription.length ? (
              <div className="homepage__copy-wrapper">
                <div
                  className={categoryCopyClasses}
                  dangerouslySetInnerHTML={{
                    __html: !isCopyExpanded
                      ? navItemData.decodeCategoryDescription[0].decodeCategoryDefaultCopy
                      : navItemData.decodeCategoryDescription[0].decodeCategoryExpandedCopy
                  }}
                />
                {!isCopyExpanded &&
                navItemData.decodeCategoryDescription[0].decodeCategoryExpandedCopy &&
                navItemData.decodeCategoryDescription[0].decodeCategoryExpandedCopy.length ? (
                  <button
                    type="button"
                    aria-label={`${navItemData.title}, read more`}
                    onClick={() => {
                      setIsCopyExpanded(true);
                    }}>
                    read more
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {decodeHomepagePosters.length ? (
          <HomepageCard categoryText={categoryText} homepagePosters={decodeHomepagePosters} />
        ) : null}
      </div>
    </>
  );
};

export default Home;
